import { useState } from "react";
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { transferRequisitionsApi, type TransferRequisitionListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M2-TR-03 — Transfer requisition list. Approval converts the requisition
 * into a draft TransferOrder; the Status column shows "Converted" when that
 * has already happened (TransferOrder linked).
 */
export function TransferRequisitionsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const query = useQuery({
    queryKey: ["transfer-requisitions", status, pagination.page, pagination.pageSize],
    queryFn: () => transferRequisitionsApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<TransferRequisitionListDto>[] = [
    { field: "documentNo", headerName: "Requisition #", width: 180 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "requestingWarehouseName", headerName: "Requested by", flex: 1, minWidth: 180 },
    { field: "suggestedSourceWarehouseName", headerName: "Suggested source", flex: 1, minWidth: 180,
      valueGetter: (_v, row) => row.suggestedSourceWarehouseName ?? "—" },
    { field: "neededByDate", headerName: "Needed by", width: 120,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "—" },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => p.row.convertedToTransferOrderId
        ? <Chip size="small" color="success" label="Converted" />
        : <Chip size="small" label={p.value as string} color={STATUS_COLOR[p.value as string] ?? "default"} /> },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Transfer requisitions</Typography>
        <Button variant="contained" onClick={() => navigate("/warehouse/transfer-requisitions/new")}>New requisition</Button>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
